import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { bootstrapCompleted } from "@/features/shell/actions";
import {
  useGetCustomerQuery,
  useGetCustomersCountQuery,
  useGetLanguagesQuery,
  useGetNotificationsCountQuery,
  useGetPlanQuery,
  useGetShopsQuery,
  useGetUserQuery,
} from "@/features/shell/api";
import { useEvaluateFeatureFlagsQuery } from "@/features/shell/featureFlagsApi";
import { ErrorScreen } from "@/features/shell/ErrorScreen";
import { LoadingScreen } from "@/features/shell/LoadingScreen";
import { readSession } from "@/shared/api/session";
import { setLocale, type Locale } from "@/shared/i18n";
import { selectIsAuthenticated, sessionRestored } from "./slice";

const NOTIFICATION_POLLING_MS = 300000;

/**
 * Sostituisce AuthBootstrap: senza token rimanda alla login; con token carica i dati di sessione,
 * applica la lingua del profilo, li consegna allo slice legacy (bootstrapCompleted) e solo dopo rende i figli.
 * Il polling del contatore notifiche parte qui e resta attivo per tutta la sessione.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const authenticated = useAppSelector(selectIsAuthenticated);
  const skip = !authenticated;
  const languages = useGetLanguagesQuery(undefined, { skip });
  const customer = useGetCustomerQuery(undefined, { skip });
  const user = useGetUserQuery(undefined, { skip });
  const plan = useGetPlanQuery(undefined, { skip });
  const shops = useGetShopsQuery(undefined, { skip });
  const unread = useGetNotificationsCountQuery(undefined, { skip, pollingInterval: NOTIFICATION_POLLING_MS });
  const customerId = customer.data?.customer_id;
  const flags = useEvaluateFeatureFlagsQuery(String(customerId ?? ""), { skip: skip || !customerId });
  useGetCustomersCountQuery(undefined, { skip });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (authenticated) return;
    // La Login legacy scrive lo storage senza passare dallo slice: si prova prima a rileggerlo.
    if (readSession().token) {
      dispatch(sessionRestored());
      return;
    }
    navigate("/", { replace: true, state: { from: location } });
  }, [authenticated, dispatch, navigate, location]);

  const queries = [languages, customer, user, plan, shops, unread, flags];
  // Solo fino al bootstrap completato: dopo, gli errori del polling notifiche sono ignorati come il
  // `.catch(() => {})` di AuthBootstrap (App.jsx:98), non devono sostituire l'app con la Connection error.
  const failed = !ready && queries.some((query) => query.isError);
  // I flag partono solo con un customer_id (come `loadFeatureFlags(customer.data.customer_id)`): senza, non si aspetta nessuno.
  const flagsReady = customerId ? flags.isSuccess : customer.isSuccess;
  const loaded =
    [languages, customer, user, plan, shops, unread].every((query) => query.isSuccess) && flagsReady;

  useEffect(() => {
    if (ready || !loaded) return;
    const languageIso = user.data?.languageIso;
    if (languageIso) setLocale(languageIso as Locale);
    dispatch(
      bootstrapCompleted({
        languages: languages.data ?? [],
        billingInfo: customer.data ?? {},
        user: user.data ?? {},
        plan: plan.data ?? [],
        unreadNotifications: unread.data?.unread ?? 0,
        shops: shops.data ?? [],
        featureFlags: flags.data ?? {},
      }),
    );
    // Unica transizione sincrona a "pronto" dopo il bootstrap (equivalente a setStatus("ready") in
    // AuthBootstrap): serve a far renderizzare i figli una sola volta, non a sincronizzare con l'esterno.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setReady(true);
  }, [
    ready,
    loaded,
    dispatch,
    languages.data,
    customer.data,
    user.data,
    plan.data,
    shops.data,
    unread.data,
    flags.data,
  ]);

  if (!authenticated) return <LoadingScreen />;
  if (failed) return <ErrorScreen onRetry={() => window.location.reload()} />;
  if (!ready) return <LoadingScreen />;
  return <>{children}</>;
}
